import type { Tracker } from '@archipro-website/tracker';
import type { AuthSource } from '~/modules/user/type';

/**
 * Funnel events for the gate pipeline, in one place so every gate reports the
 * same shape.
 *
 * The gate funnel is `opened → auth shown → authenticated → resumed → success`.
 * The middle two already have events (`LoginPromptShown` and
 * `user_login` / `new_account_registered`); these fill in the ends and the
 * Secure Sign-In hold between them, which were previously silent — drop-off
 * inside a gate could not be seen at all.
 *
 * `authSource` identifies the gate one-for-one (`enquiry`, `bulkEnquiry`,
 * `productFiles`, `designBoard`), so it is the series key on every chart and
 * travels as `Source`. Everything else rides inside `ExtraData`: only those two
 * custom keys survive the warehouse pipeline.
 */

/** How the reader entered the segment. */
export type AuthGateEntry =
    /** A guest hit the wall and has to authenticate. */
    | 'wall'
    /** Already authenticated on arrival — a social round-trip, or an auto-created account. */
    | 'authenticated';

interface AuthGateEventOpt {
    tracker: Tracker;
    authSource: AuthSource;
    data?: Record<string, unknown>;
}

const logGateEvent = (
    event:
        | 'AuthGateOpened'
        | 'AuthGateDeciding'
        | 'AuthGateResolved'
        | 'AuthGateAbandoned'
        | 'AuthGateActionResumed'
        | 'AuthGateBoardChoiceShown',
    { tracker, authSource, data }: AuthGateEventOpt,
): void => {
    const payload = { authSource, ...(data ?? {}) };
    void tracker.log(event, {
        url: new URL(window.location.href),
        data: {
            ...payload,
            Source: authSource,
            ExtraData: JSON.stringify(payload),
        },
    });
};

/** The segment took the surface. */
export const trackAuthGateOpened = (
    opt: AuthGateEventOpt & { entry: AuthGateEntry },
): void =>
    logGateEvent('AuthGateOpened', {
        ...opt,
        data: { ...opt.data, entry: opt.entry },
    });

/**
 * The post-auth hold while the Secure Sign-In offer is decided. A real round
 * trip, so a gate that stalls here is a gate losing people to latency.
 */
export const trackAuthGateDeciding = (opt: AuthGateEventOpt): void =>
    logGateEvent('AuthGateDeciding', opt);

/** The segment handed the surface back — authenticated, ask taken or declined. */
export const trackAuthGateResolved = (
    opt: AuthGateEventOpt & { offered: boolean },
): void =>
    logGateEvent('AuthGateResolved', {
        ...opt,
        data: { ...opt.data, offered: opt.offered },
    });

/** The reader left mid-segment. `step` is where they were when they went. */
export const trackAuthGateAbandoned = (
    opt: AuthGateEventOpt & { step: string },
): void =>
    logGateEvent('AuthGateAbandoned', {
        ...opt,
        data: { ...opt.data, step: opt.step },
    });

/**
 * The gated action was replayed after auth. `landed` is what the resume
 * actually reported: a resume that did not land must not be counted as a
 * conversion, and is exactly the failure this event exists to make visible.
 */
export const trackAuthGateActionResumed = (
    opt: AuthGateEventOpt & { landed: boolean },
): void =>
    logGateEvent('AuthGateActionResumed', {
        ...opt,
        data: { ...opt.data, landed: opt.landed },
    });

/** The save gate's board picker — a step between auth and success, unique to it. */
export const trackAuthGateBoardChoiceShown = (opt: AuthGateEventOpt): void =>
    logGateEvent('AuthGateBoardChoiceShown', opt);
